import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@features/auth/context/AuthContext";
import type { AuthUser } from "@features/auth/types/types";
import { hasPermission } from "@web/config/permissions";
import { apiClient } from "@lib/api/apiClient";
import type { ApiResponse } from "@lib/api/api-types";
import { SalesStructure, type StructurePerson } from "./SalesStructure";

type TeamPage = {
  items: StructurePerson[];
  pagination: { totalPages: number };
};
export function MyTeamPage() {
  const { user } = useAuth();
  return user && user.permissions.includes("leads:view_team") && user.permissions.some(p=>p==="leads:view_team" || p==="leads:view_all") ? (
    <ManagerTeam user={user} />
  ) : (
    <Alert severity="error">Team lead visibility permission required.</Alert>
  );
}
function ManagerTeam({ user }: { user: AuthUser }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const query = useQuery({
    queryKey: ["sales-reporting", "my-team", user.id],
    queryFn: async () => {
      const people: StructurePerson[] = [];
      for (let page = 1; ; page++) {
        // The server determines the manager from authentication; no manager ID is sent.
        const response = await apiClient.get<ApiResponse<TeamPage>>(
          "/sales/reporting/my-team",
          { params: { page, limit: 100 } },
        );
        people.push(...response.data.data.items);
        if (page >= response.data.data.pagination.totalPages) return people;
      }
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  const people =
    query.data?.filter((person) =>
      `${person.name} ${person.email}`
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
    ) || [];
  const canViewLeads = hasPermission(user.role, "leads:view", user.permissions);
  return (
    <Stack spacing={2}>
      <Typography variant="h4">My Team</Typography>
      <Typography color="text.secondary">
        Your designated Sales Consultants and reporting structure.
      </Typography>
      <Card sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
        >
          <TextField
            label="Search my consultants"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            size="small"
          />
          <Button
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Refresh team
          </Button>
        </Stack>
        {query.isLoading ? (
          <Typography role="status" sx={{ p: 3 }}>
            Loading your team…
          </Typography>
        ) : query.isError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {query.error.message}
          </Alert>
        ) : (
          <>
            {!query.data?.length && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No consultants are currently assigned to you. An Admin can
                manage reporting assignments.
              </Alert>
            )}
            {search && query.data?.length && !people.length ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No consultants match your search.
              </Alert>
            ) : null}
            <SalesStructure
              managers={[
                {
                  id: user.id,
                  name: user.name,
                  email: user.email,
                  profileImageUrl: user.profileImageUrl,
                },
              ]}
              consultants={people}
              readOnly
              showUnassigned={false}
              onViewLeads={
                canViewLeads
                  ? (person) => navigate(`/leads?assignedUserId=${person.id}`)
                  : undefined
              }
            />
          </>
        )}
      </Card>
    </Stack>
  );
}
